"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createDimensionalElementFromJaql = exports.createCalculatedMeasureHelper = exports.createMeasureHelper = exports.createAttributeHelper = exports.getSortType = exports.getColumnNameFromAttribute = exports.getTableNameFromAttribute = exports.createFilterFromJaql = exports.convertSort = exports.convertJaqlDataSource = exports.convertDataSource = exports.isDataSourceInfo = exports.getDataSourceName = exports.getFilterListAndRelationsJaql = exports.guidFast = void 0;
const cloneDeep_js_1 = __importDefault(require("lodash-es/cloneDeep.js"));
const mapValues_js_1 = __importDefault(require("lodash-es/mapValues.js"));
const filter_from_jaql_util_js_1 = require("./dimensional-model/filters/utils/filter-from-jaql-util.js");
const types_js_1 = require("./dimensional-model/types.js");
const sdk_common_1 = require("@sisense/sdk-common");
const measures_js_1 = require("./dimensional-model/measures/measures.js");
const filters_js_1 = require("./dimensional-model/filters/filters.js");
const attributes_js_1 = require("./dimensional-model/attributes.js");
const simple_column_types_js_1 = require("./dimensional-model/simple-column-types.js");
/**
 * A more performant, but slightly bulkier, RFC4122v4 implementation. Performance is improved by minimizing calls to random()
 *
 * @internal
 */
const guidFast = function (len) {
    if (!len) {
        len = 20;
    }
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
    const uuid = new Array(len);
    let rnd = 0;
    let r;
    for (let i = 0; i < len; i += 1) {
        if (i > 0 && i % 5 == 0) {
            uuid[i] = '-';
            continue;
        }
        if (rnd <= 0x02) {
            rnd = (0x2000000 + Math.random() * 0x1000000) | 0;
        }
        r = rnd & 0xf;
        rnd = rnd >> 4;
        uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r];
    }
    return uuid.join('');
};
exports.guidFast = guidFast;
/**
 * Function to separate list of filters from the provided relation and get logical relations for jaql
 *
 * @internal
 */
const getFilterListAndRelationsJaql = (filterRelations) => {
    if (!filterRelations) {
        return { filters: undefined, relations: undefined };
    }
    if (Array.isArray(filterRelations)) {
        return { filters: filterRelations, relations: undefined };
    }
    const filters = new Set();
    function traverseCascade(cascade) {
        const [firstFilter, ...restFilters] = cascade;
        filters.add(firstFilter);
        if (!restFilters.length) {
            return traverse({ instanceid: firstFilter.config.guid });
        }
        const left = traverse(firstFilter);
        const right = traverseCascade(restFilters);
        return { left, right, operator: 'AND' };
    }
    function traverse(node) {
        if (!node)
            return node;
        if (isFilter(node)) {
            if ((0, filters_js_1.isCascadingFilter)(node)) {
                return traverseCascade(node.filters);
            }
            filters.add(node);
            return { instanceid: node.config.guid };
        }
        else {
            if ('left' in node) {
                node.left = traverse(node.left);
            }
            if ('right' in node) {
                node.right = traverse(node.right);
            }
            return node;
        }
    }
    // Create a deep copy of filterRelations to avoid mutation
    const copiedFilterRelations = (0, cloneDeep_js_1.default)(filterRelations);
    const relations = traverse(copiedFilterRelations);
    return { filters: Array.from(filters), relations };
};
exports.getFilterListAndRelationsJaql = getFilterListAndRelationsJaql;
function isFilter(node) {
    return 'config' in node && 'guid' in node.config;
}
/**
 * Gets the name of the data source
 *
 * @internal
 */
function getDataSourceName(dataSource) {
    return typeof dataSource === 'string' ? dataSource : dataSource.title;
}
exports.getDataSourceName = getDataSourceName;
/**
 * Checks if the provided 'dataSource' is a data source info structure that contains more than just the data source name.
 *
 * @internal
 */
function isDataSourceInfo(dataSource) {
    return typeof dataSource === 'object' && 'type' in dataSource && 'title' in dataSource;
}
exports.isDataSourceInfo = isDataSourceInfo;
/**
 * Converts a JaqlDataSource to DataSource.
 *
 * @internal
 */
function convertDataSource(jaqlDataSource) {
    return {
        id: jaqlDataSource.id,
        address: jaqlDataSource.address,
        title: jaqlDataSource.title,
        type: jaqlDataSource.live ? 'live' : 'elasticube',
    };
}
exports.convertDataSource = convertDataSource;
/**
 * Converts a DataSource to a description of data source used in JAQL.
 *
 * @internal
 */
function convertJaqlDataSource(dataSource) {
    return isDataSourceInfo(dataSource)
        ? {
            title: dataSource.title,
            live: dataSource.type === 'live',
        }
        : {
            title: dataSource,
            live: false,
        };
}
exports.convertJaqlDataSource = convertJaqlDataSource;
/**
 * Converts a string to a Sort enum
 *
 * @param sort - The string to convert
 * @returns The converted Sort enum
 * @internal
 */
function convertSort(sort) {
    if (sort) {
        return sort === 'asc' ? types_js_1.Sort.Ascending : types_js_1.Sort.Descending;
    }
    else {
        return types_js_1.Sort.None;
    }
}
exports.convertSort = convertSort;
/**
 * Creates a filter from a JAQL object.
 *
 * @param jaql - The filter JAQL object.
 * @param instanceid - Optional instance ID.
 * @param disabled - Optional disabled flag.
 * @param locked - Optional locked flag.
 * @returns - The created Filter object.
 * @internal
 */
const createFilterFromJaql = (jaql, instanceid, disabled = false, locked = false) => {
    // translation logic is based on FilterJaqlInternal type (from internal modern-analytics-filters)
    // TODO reconcile FilterJaql and FilterJaqlInternal
    const hasBackgroundFilter = jaql.filter.filter && !('turnedOff' in jaql.filter.filter);
    const guid = instanceid || (0, exports.guidFast)();
    const filter = (0, filter_from_jaql_util_js_1.createFilterFromJaqlInternal)(jaql, guid);
    if (hasBackgroundFilter) {
        const backgroundFilter = (0, filter_from_jaql_util_js_1.createFilterFromJaqlInternal)(Object.assign(Object.assign({}, jaql), { filter: jaql.filter.filter }), `${instanceid}-bg`);
        const config = filter.config;
        filter.config = Object.assign(Object.assign({}, config), { backgroundFilter });
        return filter;
    }
    filter.config = Object.assign(Object.assign({}, filter.config), { originalFilterJaql: jaql, disabled, locked });
    return filter;
};
exports.createFilterFromJaql = createFilterFromJaql;
/**
 * Extracts the table and column names from the given expression string.
 *
 * @internal
 */
function parseExpression(expression) {
    const [table, column] = expression.slice(1, -1).split('.');
    return {
        table,
        column: column
            ? column
                // in case of Date we have to remove the (Calendar) part
                .replace('(Calendar)', '')
                .trim()
            : '',
    };
}
/**
 * Retrieves the table value from the attribute.
 *
 * @internal
 */
function getTableNameFromAttribute(attribute) {
    return parseExpression(attribute.expression).table;
}
exports.getTableNameFromAttribute = getTableNameFromAttribute;
/**
 * Retrieves the column value from the attribute.
 *
 * @internal
 */
function getColumnNameFromAttribute(attribute) {
    return parseExpression(attribute.expression).column;
}
exports.getColumnNameFromAttribute = getColumnNameFromAttribute;
/**
 * Gets the sort type based on the jaql sort direction.
 *
 * @param jaqlSort - The jaql sort direction.
 * @returns  The sort direction.
 * @internal
 */
function getSortType(jaqlSort) {
    switch (jaqlSort) {
        case types_js_1.JaqlSortDirection.ASC:
            return 'sortAsc';
        case types_js_1.JaqlSortDirection.DESC:
            return 'sortDesc';
        default:
            return 'sortNone';
    }
}
exports.getSortType = getSortType;
const DATA_MODEL_MODULE_NAME = 'DM';
/**
 * Creates an attribute or level attribute from the provided parameters
 *
 * @returns attribute or level attribute
 * @internal
 */
const createAttributeHelper = ({ dim, table, column, dataType, level, format, sort, title, panel, dataSource, }) => {
    // if table is undefined, extract it from dim
    const dimTable = table !== null && table !== void 0 ? table : parseExpression(dim).table;
    // if column is undefined, extract it from dim
    const dimColumn = column !== null && column !== void 0 ? column : parseExpression(dim).column;
    const sortEnum = convertSort(sort);
    const isDataTypeDatetime = dataType === types_js_1.DataType.DATETIME;
    if (isDataTypeDatetime) {
        const dateLevel = attributes_js_1.DimensionalLevelAttribute.translateJaqlToGranularity({ level });
        const composeCode = (0, attributes_js_1.normalizeAttributeName)(dimTable, dimColumn, level, DATA_MODEL_MODULE_NAME);
        const levelAttribute = new attributes_js_1.DimensionalLevelAttribute(title !== null && title !== void 0 ? title : dimColumn, dim, dateLevel, format || attributes_js_1.DimensionalLevelAttribute.getDefaultFormatForGranularity(dateLevel), undefined, sortEnum, dataSource, composeCode, panel);
        return levelAttribute;
    }
    const attributeType = !dataType || (0, simple_column_types_js_1.isNumber)(dataType) ? types_js_1.MetadataTypes.NumericAttribute : types_js_1.MetadataTypes.TextAttribute;
    const composeCode = (0, attributes_js_1.normalizeAttributeName)(dimTable, dimColumn, undefined, DATA_MODEL_MODULE_NAME);
    const attribute = new attributes_js_1.DimensionalAttribute(title !== null && title !== void 0 ? title : dimColumn, dim, attributeType, undefined, sortEnum, dataSource, composeCode, panel);
    return attribute;
};
exports.createAttributeHelper = createAttributeHelper;
/**
 * Creates a measure from the provided parameters
 *
 * @returns measure
 * @internal
 */
const createMeasureHelper = ({ dim, table, column, dataType, agg, level, format, sort, title, dataSource, }) => {
    const sortEnum = convertSort(sort);
    const attribute = (0, exports.createAttributeHelper)({
        dim,
        table,
        column,
        dataType,
        level,
        format,
        sort,
        title,
        dataSource,
    });
    const tranformedAgg = measures_js_1.DimensionalBaseMeasure.aggregationFromJAQL(agg);
    const updatedTitle = title !== null && title !== void 0 ? title : `${tranformedAgg} ${column}`;
    // currently, sort and format applied to attribute but not to measure
    const composeCode = `measureFactory.${tranformedAgg}(${attribute.composeCode}, '${(0, sdk_common_1.escapeSingleQuotes)(updatedTitle)}')`;
    const measure = new measures_js_1.DimensionalBaseMeasure(updatedTitle, attribute, tranformedAgg, undefined, undefined, sortEnum, composeCode);
    return measure;
};
exports.createMeasureHelper = createMeasureHelper;
const getContextComposeCode = (context) => {
    return ('{' +
        Object.entries(context).reduce((acc, [key, value]) => {
            acc =
                acc +
                    `'${key.slice(1, -1)}': ${value && 'composeCode' in value ? value.composeCode : JSON.stringify(value)},`;
            return acc;
        }, '') +
        '}');
};
/**
 * Creates a measure from the provided parameters
 *
 * @returns calculated measure
 * @internal
 */
const createCalculatedMeasureHelper = (jaql) => {
    var _a;
    const sortEnum = convertSort(jaql.sort);
    const context = (0, mapValues_js_1.default)((_a = jaql.context) !== null && _a !== void 0 ? _a : {}, (jaqlContextValue) => {
        if (typeof jaqlContextValue === 'string') {
            return jaqlContextValue;
        }
        return jaqlContextValue && createDimensionalElementFromJaql(jaqlContextValue);
    });
    const composeCode = `measureFactory.customFormula('${(0, sdk_common_1.escapeSingleQuotes)(jaql.title)}', '${jaql.formula}', ${getContextComposeCode(context)})`;
    return new measures_js_1.DimensionalCalculatedMeasure(jaql.title, jaql.formula, context, undefined, undefined, sortEnum, composeCode);
};
exports.createCalculatedMeasureHelper = createCalculatedMeasureHelper;
/**
 * Creates a dimensional element from a JAQL object.
 *
 * @param jaql - The JAQL object.
 * @param datetimeFormat - The datetime format.
 * @returns The created dimensional element.
 * @internal
 */
function createDimensionalElementFromJaql(jaql, datetimeFormat, panel) {
    const isFilterJaql = 'filter' in jaql;
    if (isFilterJaql) {
        return (0, exports.createFilterFromJaql)(jaql);
    }
    const isFormulaJaql = 'formula' in jaql;
    if (isFormulaJaql) {
        return (0, exports.createCalculatedMeasureHelper)(jaql);
    }
    const dataSource = 'datasource' in jaql ? jaql.datasource : undefined;
    const hasAggregation = !!jaql.agg;
    if (hasAggregation) {
        return (0, exports.createMeasureHelper)({
            dim: jaql.dim,
            table: jaql.table,
            column: jaql.column,
            dataType: jaql.datatype,
            agg: jaql.agg || '',
            level: jaql.level,
            format: datetimeFormat,
            sort: jaql.sort,
            title: jaql.title,
            dataSource,
        });
    }
    return (0, exports.createAttributeHelper)({
        dim: jaql.dim,
        table: jaql.table,
        column: jaql.column,
        dataType: jaql.datatype,
        level: jaql.level,
        format: datetimeFormat,
        sort: jaql.sort,
        title: jaql.title,
        panel: panel,
        dataSource,
    });
}
exports.createDimensionalElementFromJaql = createDimensionalElementFromJaql;
